import React, { useEffect, useState } from "react";
import moment from "moment";
import { mdiTimerCog } from "@mdi/js";
import { IoArrowBack } from "react-icons/io5";
import MdiIcon from "../../../components/device/app/MdiIcon";
import { TRAITS } from "../../../constants/devices";
import { sendControl } from "../../../services/deviceControl";
import { UserService } from "../../../services/UserService";
import { User } from "../../../storage/store";
import "../../../assets/styles/app-security.css";

// The pages behind the rows of the "Accessory Information" sheet
// (app/components/SwitchInfo.js).

export const InfoBack = ({ onBack }) => (
  <button type="button" className="hx-info-back" onClick={onBack}>
    <IoArrowBack aria-hidden="true" />
    Accessory Information
  </button>
);

// ---------- Recent changes to settings ----------

const shown = (value) => (value !== null && typeof value === "object" ? JSON.stringify(value) : String(value));

// What changed from one saved version of the settings to the next, newest
// first, as the app lists them. The first version has nothing to compare with.
export const settingChanges = (records) => {
  const versions = [...(records ?? [])]
    .sort((a, b) => new Date(a.time) - new Date(b.time))
    .map((item) => ({
      time: item.time,
      values: item.device_settings && Object.keys(item.device_settings).length ? item.device_settings : item.motor_config ?? {},
    }));
  const changes = [];
  versions.forEach((version, index) => {
    if (index === 0) return;
    const before = versions[index - 1].values;
    const changed = Object.entries(version.values).filter(([key, value]) => shown(value) !== shown(before[key]));
    if (changed.length) changes.push({ time: version.time, changed });
  });
  return changes.reverse();
};

export const SettingsChanges = ({ changes, onBack }) => (
  <div className="hx-info">
    <InfoBack onBack={onBack} />
    {changes.length ? (
      <ol className="hx-info-changes">
        {changes.map((change) => (
          <li key={change.time}>
            <p className="hx-info-change-time">
              <MdiIcon path={mdiTimerCog} size={20} />
              {moment(change.time).format("DD-MM-YYYY hh:mm A")}
            </p>
            <ul>
              {change.changed.map(([key, value]) => (
                <li key={key}>
                  {key} : {shown(value)}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ol>
    ) : (
      <p className="hx-modal-text">No recent changes to settings.</p>
    )}
  </div>
);

// ---------- Warranty info ----------

const WARRANTY_TYPES = [
  {
    title: "Replacement Warranty",
    description: "Products under this warranty will be replaced if found defective within the specified warranty period.",
  },
  {
    title: "Limited Service Warranty",
    description: "Products under this warranty will be serviced and repaired if found defective within the warranty period.",
  },
  {
    title: "Service-Ended Warranty",
    description:
      "This warranty covers the period up to the end of the limited service warranty. After this period, all service support, including repairs and replacements, will be discontinued.",
  },
];

// Which of the three the device is under (app/screens/WarrantyInfo.js).
const warrantyStatus = (warranty) => {
  const ended = new Date(warranty.end_date) < new Date();
  if (warranty.serviceEnabled === true) return ended ? "Limited Service Warranty" : "Replacement Warranty";
  return "Service-Ended Warranty";
};

const longDate = (value) =>
  new Date(value).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

export const WarrantyInfo = ({ device, onBack }) => {
  const [warranty, setWarranty] = useState(undefined);

  useEffect(() => {
    let current = true;
    UserService.getWarranty(device.deviceId)
      .then((result) => current && setWarranty(result ?? null))
      .catch(() => current && setWarranty(null));
    return () => {
      current = false;
    };
  }, [device.deviceId]);

  const rows = warranty?.duration
    ? [
        ["Model", warranty.model],
        ["Status", warrantyStatus(warranty)],
        ["Activated on", longDate(warranty.start_date)],
        ["Valid until", longDate(warranty.end_date)],
      ]
    : null;

  return (
    <div className="hx-info">
      <InfoBack onBack={onBack} />
      <div className="hx-warranty-card">
        <h3>E-Warranty Card</h3>
        {warranty === undefined ? (
          <p role="status">Loading…</p>
        ) : rows ? (
          <dl>
            {rows.map(([title, value]) => (
              <div key={title}>
                <dt>{title}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p>Warranty not found for this device.</p>
        )}
      </div>
      <h3 className="hx-warranty-title">Warranty Information</h3>
      <p className="hx-warranty-text">At HASHh Automations, we offer three types of warranty services for our products :</p>
      <ul className="hx-warranty-types">
        {WARRANTY_TYPES.map((type) => (
          <li key={type.title}>
            <b>{type.title}</b>
            <p className="hx-warranty-text">{type.description}</p>
          </li>
        ))}
      </ul>
    </div>
  );
};

// ---------- Security System Info (WZLTEG_1) ----------

const known = (value) => value !== undefined && value !== null && value !== "";

// The gateway's readings (app/screens/SecurityDeviceInfo.js). A reading the
// gateway hasn't sent yet shows as "—" rather than the app's defaults.
const securityRows = (info) => [
  {
    title: "GSM Signal Strength",
    icon: "gsm",
    value: !known(info?.gsm_signal) ? "—" : Number(info.gsm_signal) === 0 ? "N/A" : info.gsm_signal,
    description: "1 to 10: Poor, 10 to 20: Fair, 20 to 25: Good, 25 to 31: Excellent, N/A: Sim card not found.",
  },
  {
    title: "AC Power Supply",
    icon: "ac",
    value: !known(info?.ac_power) ? "—" : Number(info.ac_power) === 0 ? "OFF" : Number(info.ac_power) === 1 ? "ON" : "Fault",
    description: "You can view the AC power status to check if the Control Panel is receiving line power.",
  },
  {
    title: "Battery Status",
    icon: "battery",
    value: !known(info?.bts) ? "—" : Number(info.bts) === 0 ? "Low" : "Normal",
    description: "The battery level shows as either 'Full' or 'Low'.",
  },
  { title: "IMEI", icon: "imei", value: info?.gsm_imei ?? "Null" },
];

// Asks the gateway for fresh readings (1301 id 5, as the app does when its
// info opens), then reads them back a moment later.
export const SecurityInfo = ({ device, lastState, onBack }) => {
  const [info, setInfo] = useState(lastState);
  const [refreshing, setRefreshing] = useState(true);

  useEffect(() => {
    let current = true;
    const refresh = async () => {
      try {
        await sendControl({
          on: [device.applianceId],
          off: [],
          action: { [device.applianceId]: { id: 5, trait: Number(TRAITS.SECURITY_MODE), value: 0 } },
        });
        await new Promise((resolve) => setTimeout(resolve, 2500));
      } catch (error) {
        // Still read what the server has.
      }
      try {
        const latest = await UserService.getLastStatebydeviceId(User.getPropertyId(), device.deviceId);
        if (current && latest) setInfo(latest);
      } catch (error) {
        // Keep the readings the page already had.
      }
      if (current) setRefreshing(false);
    };
    refresh();
    return () => {
      current = false;
    };
  }, [device]);

  return (
    <div className="hx-info">
      <InfoBack onBack={onBack} />
      <p className="hx-sec-info-status" role="status">
        {refreshing ? "Getting the latest readings…" : ""}
      </p>
      <ul className="hx-sec-info">
        {securityRows(info).map((row) => {
          const [imei, detail] = row.title === "IMEI" && info?.gsm_imei ? String(info.gsm_imei).split("(") : [];
          return (
            <li key={row.title}>
              <span className="hx-sec-info-icon" aria-hidden="true" style={{ "--hx-sec-icon": `url(/security_device/${row.icon}.png)` }} />
              <div className="hx-sec-info-text">
                <div className="hx-sec-info-line">
                  <span className="hx-sec-info-title">{row.title}</span>
                  <span className="hx-sec-info-value">
                    {imei !== undefined ? (
                      <>
                        {imei}
                        {detail ? <small>({detail}</small> : null}
                      </>
                    ) : (
                      row.value
                    )}
                  </span>
                </div>
                {row.description ? <p className="hx-sec-info-desc">{row.description}</p> : null}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
